import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * "You have unsaved changes" — on every way out of a page, not just one.
 *
 * Losing an hour of design work to a stray ⌘R is the kind of bug users only
 * report once, so this covers all three exits an editor actually has:
 *
 *  1. TAB CLOSE / RELOAD / typing another address — `beforeunload`, which is the
 *     only one the browser draws itself (and the only wording it accepts: the
 *     custom message was dropped by every browser years ago).
 *  2. IN-APP NAVIGATION — a router push/replace from a link or a back arrow in
 *     the app's own chrome. React Router 6 without a data router has no blocker
 *     API, so the navigator's push/replace are wrapped for as long as the guard
 *     is armed. Same trick the image editor already uses.
 *  3. BROWSER BACK / FORWARD — a POP cannot be cancelled once it has happened,
 *     so a sentinel entry pointing at the SAME url is pushed while the guard is
 *     armed. Back then lands on the current page (nothing visibly moves), the
 *     confirm is asked, and either a second back completes the exit or the
 *     sentinel is pushed again to hold position.
 *
 * Disarming (a save finished, the component unmounted) removes the sentinel, so
 * the history depth the user sees is the depth they had.
 *
 * @param {boolean} when  Guard only while this is true — pass false while a save
 *                        is in flight, or a navigation the save itself performs
 *                        would prompt the user about their own click.
 * @param {string} message Confirm text for the two in-app cases.
 */
const DEFAULT_MESSAGE = 'You have unsaved changes. Leave this page and lose them?';

export default function useUnsavedExitGuard(when, message = DEFAULT_MESSAGE) {
    const { navigator } = useContext(NavigationContext);

    // 1. Tab close, reload, address bar.
    useEffect(() => {
        if (!when) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            // Legacy browsers still want a returnValue set to show their dialog.
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [when]);

    // 2. In-app navigation (links, the editor's own back arrow).
    useEffect(() => {
        if (!when || !navigator) return undefined;
        const push = navigator.push.bind(navigator);
        const replace = navigator.replace.bind(navigator);
        navigator.push = (...args) => { if (window.confirm(message)) push(...args); };
        navigator.replace = (...args) => { if (window.confirm(message)) replace(...args); };
        return () => {
            navigator.push = push;
            navigator.replace = replace;
        };
    }, [when, navigator, message]);

    // 3. Browser / Android back and forward.
    useEffect(() => {
        if (!when) return undefined;
        let armed = true;
        // Each arming stamps its OWN id. Cleanup then only ever takes back the
        // entry it pushed: a stale flag left over from an earlier guard (or from
        // another editor on another page) must not make this one pop history.
        const id = `${Date.now()}.${Math.random().toString(36).slice(2)}`;
        // Carry the router's own history state along (it tracks `idx` there), so
        // the sentinel is invisible to it beyond being one more same-url entry.
        const sentinel = () => window.history.pushState(
            { ...(window.history.state || {}), __exitGuard: id },
            '',
            window.location.href,
        );
        sentinel();

        const onPop = () => {
            if (!armed) return;
            if (window.confirm(message)) {
                // Let the exit through: the entry under the sentinel is where the
                // user was actually heading.
                armed = false;
                window.removeEventListener('popstate', onPop);
                window.history.back();
            } else {
                sentinel();
            }
        };
        window.addEventListener('popstate', onPop);

        return () => {
            window.removeEventListener('popstate', onPop);
            // Saved, or unmounted some other way — take the sentinel back out so a
            // later Back press doesn't land on this same page first.
            if (armed && window.history.state?.__exitGuard === id) window.history.back();
        };
    }, [when, message]);
}
